import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { DiagnosticTabProps } from "@/components/projects/diagnostic/DiagnosticSection";
import { useCurrentUser } from "@/hooks/useAuth";
import {
  useBatchOrgValidation,
  useDiagnosticOrgStructure,
  useUpdateOrgValidation,
} from "@/hooks/useDiagnosticOrgStructure";
import { hasMinRole } from "@/lib/roles";

import { OrgStructureView } from "./OrgStructureView";

/**
 * O detalhe "Estrutura organizacional" do Ato 1 (EF-DIAG-002, #1342).
 *
 * Os outros detalhes recebem tudo por props, mas a árvore não está no snapshot do
 * Diagnóstico, que traz só o agregado do tile. Este invólucro faz a leitura dela,
 * e a `OrgStructureView` continua só desenhando.
 */
export function OrgStructureDetail(_props: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const { data, isLoading, refetch } = useDiagnosticOrgStructure(projectId);
  const update = useUpdateOrgValidation(projectId);
  const batch = useBatchOrgValidation(projectId);
  // O PUT exige `consultant`; o papel `user` vê as decisões sem poder mexer, em
  // vez de clicar e receber 403.
  const canEdit = hasMinRole(useCurrentUser()?.role, "consultant");

  if (isLoading) {
    return <p className="text-[13px] text-[#596b85]">{t("Loading…")}</p>;
  }

  // Falha não é árvore vazia: dizer "ainda não foi lida" aqui afirmaria algo
  // sobre o projeto que a tela não sabe. E só vale sem dado: quando uma releitura
  // falha, o TanStack mantém a árvore anterior, e trocá-la pelo erro jogaria fora
  // o filtro e as linhas abertas no meio da reunião.
  if (!data) {
    return (
      <div
        data-testid="org-structure-error"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-[#f3c7c2] bg-[#fdf3f2] px-4 py-3 text-[13px] text-[#a23b2f]"
      >
        {t("Could not load the organizational structure.")}
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-md border border-current px-3 py-1 text-[12px] font-semibold"
        >
          {t("Try again")}
        </button>
      </div>
    );
  }

  return (
    <OrgStructureView
      tree={data}
      canEdit={canEdit}
      onSave={update.mutateAsync}
      onSaveBatch={batch.mutateAsync}
    />
  );
}
